/**
 * Klasse PORTAL_HINTERGRUND_ANALYSE. Fail-closed.
 * Untertab im Reiter Dokumente. Kein Hauptnav-Punkt. Kein Tarif.
 * „In Chat öffnen“ nur bei exakt ARKO = freigeschaltet.
 */

export const HINTERGRUND_ANALYSE_TAB = 'hintergrund';
export const DOKUMENTE_LEISTE = ['schnellzugriff', 'favoriten', 'hintergrund'] as const;
export type DokumenteLeisteTab = (typeof DOKUMENTE_LEISTE)[number];

export const ARKO_BAUSTEIN_KEY = 'ARKO';
export const ARKO_BAUSTEIN_FREI = 'freigeschaltet';
export const ANALYSE_CHAT_TITLE_PREFIX = 'Analyse - ';

const STORE_PREFIX = 'arkorbit.portal.hintergrundAnalyse.v1:';
const META_PREFIX = 'arkorbit.portal.hintergrundAnalyse.meta.v1:';
const ARKO_THREADS_PREFIX = 'arkorbit_portal_arko_threads_v1__';
const MAX_OPEN_CHATS = 10;

export type HintergrundStatus = 'running' | 'done' | 'error' | 'cancelled';

export type HintergrundLauf = {
  id: string;
  documentName: string;
  text: string;
  marked: boolean;
  status: HintergrundStatus;
};

export type HintergrundMeta = {
  text?: string;
  marked?: boolean;
  hidden?: boolean;
};

type LiveLauf = {
  request_id: string;
  documentName: string;
  status: string;
  workerText?: string | null;
};

type StoredWorkspace = {
  version: 1;
  activeThreadIds: string[];
  activeId: string;
  threads: Record<string, {
    id: string;
    title: string;
    messages: Array<{ role: 'bot'; text: string; id: string }>;
    updatedAt: number;
  }>;
  archived: Record<string, unknown>;
};

function parseMap(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  return null;
}

/** Nur der Schlüssel ARKO mit dem Wert freigeschaltet. Feld fehlt oder anders: aus. */
export function arkoExactFreigeschaltet(raw: unknown): boolean {
  const map = parseMap(raw);
  if (!map || !Object.prototype.hasOwnProperty.call(map, ARKO_BAUSTEIN_KEY)) return false;
  const value = map[ARKO_BAUSTEIN_KEY];
  if (typeof value !== 'string') return false;
  return value.trim() === ARKO_BAUSTEIN_FREI;
}

export function analyseChatTitle(filename: string): string {
  const name = filename.trim() || 'Dokument';
  return `${ANALYSE_CHAT_TITLE_PREFIX}${name}`;
}

function asStatus(value: string): HintergrundStatus {
  if (value === 'running' || value === 'done' || value === 'error' || value === 'cancelled') return value;
  return 'done';
}

function storeKey(customerId: string): string {
  return `${STORE_PREFIX}${customerId}`;
}

function metaKey(customerId: string): string {
  return `${META_PREFIX}${customerId}`;
}

function readJson<T>(key: string): T | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* privater Modus */
  }
}

export function loadHintergrundMeta(customerId: string): Record<string, HintergrundMeta> {
  const parsed = readJson<Record<string, HintergrundMeta>>(metaKey(customerId));
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return parsed;
}

export function saveHintergrundMeta(customerId: string, meta: Record<string, HintergrundMeta>): void {
  writeJson(metaKey(customerId), meta);
}

export function loadHintergrundStored(customerId: string): HintergrundLauf[] {
  const parsed = readJson<HintergrundLauf[]>(storeKey(customerId));
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((row) => row && typeof row.id === 'string' && row.id.trim());
}

export function saveHintergrundStored(customerId: string, rows: HintergrundLauf[]): void {
  writeJson(storeKey(customerId), rows);
}

export function mergeHintergrundLaeufe(
  stored: readonly HintergrundLauf[],
  live: readonly LiveLauf[],
  meta: Record<string, HintergrundMeta>,
): HintergrundLauf[] {
  const byId = new Map<string, HintergrundLauf>();
  for (const row of stored) {
    const id = row.id.trim();
    if (!id || meta[id]?.hidden) continue;
    const edited = meta[id]?.text;
    byId.set(id, {
      id,
      documentName: row.documentName?.trim() || 'Dokument',
      text: typeof edited === 'string' ? edited : (row.text || ''),
      marked: meta[id]?.marked === true || row.marked === true,
      status: asStatus(row.status),
    });
  }
  for (const row of live) {
    const id = row.request_id.trim();
    if (!id || meta[id]?.hidden) continue;
    const prev = byId.get(id);
    const edited = meta[id]?.text;
    byId.set(id, {
      id,
      documentName: row.documentName?.trim() || prev?.documentName || 'Dokument',
      text: typeof edited === 'string' ? edited : (row.workerText?.trim() || prev?.text || ''),
      marked: meta[id]?.marked === true || prev?.marked === true,
      status: asStatus(row.status),
    });
  }
  return Array.from(byId.values());
}

export function rememberHintergrundLaeufe(customerId: string, live: readonly LiveLauf[]): HintergrundLauf[] {
  const id = customerId.trim();
  if (!id) return [];
  const meta = loadHintergrundMeta(id);
  const merged = mergeHintergrundLaeufe(loadHintergrundStored(id), live, meta);
  saveHintergrundStored(id, merged);
  return merged;
}

export function editHintergrundText(
  meta: Record<string, HintergrundMeta>,
  id: string,
  text: string,
): Record<string, HintergrundMeta> {
  const key = id.trim();
  if (!key) return meta;
  return { ...meta, [key]: { ...meta[key], text, hidden: false } };
}

export function deleteHintergrund(
  meta: Record<string, HintergrundMeta>,
  id: string,
): Record<string, HintergrundMeta> {
  const key = id.trim();
  if (!key) return meta;
  return { ...meta, [key]: { ...meta[key], hidden: true } };
}

export function toggleHintergrundMark(
  meta: Record<string, HintergrundMeta>,
  id: string,
  marked: boolean,
): Record<string, HintergrundMeta> {
  const key = id.trim();
  if (!key) return meta;
  return { ...meta, [key]: { ...meta[key], marked, hidden: false } };
}

export function nextArkoWorkspace(
  prev: StoredWorkspace | null,
  filename: string,
  text: string,
  now = Date.now(),
): StoredWorkspace {
  const id = `analyse_${now.toString(36)}`;
  const title = analyseChatTitle(filename);
  const body = text.trim();
  const thread = {
    id,
    title,
    messages: body ? [{ role: 'bot' as const, text: body, id: `analyse_msg_${now.toString(36)}` }] : [],
    updatedAt: now,
  };
  const base: StoredWorkspace = prev && prev.version === 1 && prev.threads
    ? {
        version: 1,
        activeThreadIds: Array.isArray(prev.activeThreadIds) ? [...prev.activeThreadIds] : [],
        activeId: prev.activeId || '',
        threads: { ...prev.threads },
        archived: prev.archived && typeof prev.archived === 'object' ? prev.archived : {},
      }
    : { version: 1, activeThreadIds: [], activeId: '', threads: {}, archived: {} };
  base.threads[id] = thread;
  const ids = base.activeThreadIds.filter((row) => row !== id && base.threads[row]);
  ids.push(id);
  while (ids.length > MAX_OPEN_CHATS) ids.shift();
  base.activeThreadIds = ids;
  base.activeId = id;
  return base;
}

/** Legt die Analyse zusätzlich in den ARKO-Chat. Titel „Analyse - “ plus Dateiname. */
export function placeAnalysisInArkoChat(customerId: string, filename: string, text: string): string | null {
  const seat = customerId.trim();
  if (!seat || typeof localStorage === 'undefined') return null;
  const key = `${ARKO_THREADS_PREFIX}${seat}`;
  let prev: StoredWorkspace | null = null;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredWorkspace;
      if (parsed && parsed.version === 1 && parsed.threads) prev = parsed;
    }
  } catch {
    prev = null;
  }
  const next = nextArkoWorkspace(prev, filename, text);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    return null;
  }
  return next.activeId;
}
